import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Select, Card, Row, Col, Typography, Spin } from "antd";
import { useLocation, useHistory } from "react-router-dom";
import { Button } from "antd";
import { FilterOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { sliceKey, reducer, actions } from "./slice";
import { dashboardFiltersSaga } from "./saga";
import { useAuth } from "../../../../contexts/auth";
import * as selectors from "./selectors";

const { Text } = Typography;

const tabs = [
  { key: "overall", label: "Overall" },
  { key: "keyParameters", label: "Key Parameters" },
];

export default function DashboardFilter({
  selectedClients,
  selectedProjects,
  handleClientChange,
  handleProjectChange,
  extraStateOptions = [],
  selectedState,
  handleStateChange,
  showStateDropdown = false,
  showCompletionSort = false,
  onToggleSort,
  isSortedDescending,
  showRegretDropdown = false,
  // New props for param page filters
  selectedCommodities,
  handleCommodityChange,
  selectedTestingTypes,
  handleTestingTypeChange,
  showParamDropdowns = false,
  currentView,
  parameterRequest,
  handleParameterRequest
}) {
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: dashboardFiltersSaga });

  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const auth = useAuth();
  const user = auth.loggedInUser;
  const userRole = user?.role;

  const clients = useSelector(selectors.selectClients);
  const projects = useSelector(selectors.selectProjects);
  const rawProjects = useSelector(selectors.selectRawProjects);
  const commodities = useSelector(selectors.selectCommodities);
  const testingTypes = useSelector(selectors.selectTestingTypes);
  const [isProjectsLoading, setIsProjectsLoading] = useState(false);

  // Memoize selectedClients array to prevent unnecessary re-renders
  const memoizedSelectedClients = useMemo(
    () => selectedClients || [],
    [selectedClients]
  );

  const memoizedSelectedProjects = useMemo(
    () => selectedProjects || [],
    [selectedProjects]
  );

  const memoizedSelectedCommodities = useMemo(
    () => selectedCommodities || [],
    [selectedCommodities]
  );

  const memoizedSelectedTestingTypes = useMemo(
    () => selectedTestingTypes || [],
    [selectedTestingTypes]
  );

  useEffect(() => {
    dispatch(actions.fetchClients());
    // Fetch commodities and testing types if param dropdowns should be shown
    if (showParamDropdowns) {
      dispatch(actions.fetchCommodities());
      dispatch(actions.fetchTestingTypes());
    }
  }, [dispatch, showParamDropdowns]);

  // Fetch projects when selectedClients changes
  useEffect(() => {
    if (memoizedSelectedClients.length > 0) {
      setIsProjectsLoading(true);
      const query = memoizedSelectedClients
        .map((id) => `companyId=${id}`)
        .join("&");
      dispatch(actions.fetchProjects({ query }));
    } else {
      dispatch(actions.clearProjects());
    }
  }, [dispatch, memoizedSelectedClients]);

  // Stop loading when projects are received
  useEffect(() => {
    if (projects !== undefined) {
      setIsProjectsLoading(false);
    }
  }, [projects]);

  // Smart deselect logic - filter projects when they are no longer available
  useEffect(() => {
    if (
      rawProjects &&
      rawProjects.length > 0 &&
      memoizedSelectedProjects.length > 0
    ) {
      const validProjects = selectors.getValidProjects(
        memoizedSelectedProjects,
        memoizedSelectedClients,
        rawProjects
      );
      // If some projects were filtered out, update the parent
      if (validProjects.length !== memoizedSelectedProjects.length) {
        handleProjectChange(validProjects);
      }
    }
  }, [
    rawProjects,
    memoizedSelectedClients,
    memoizedSelectedProjects,
    handleProjectChange,
  ]);

  // Wrapper for client change to handle smart deselect
  const handleClientChangeWrapper = useCallback(
    (newSelectedClients) => {
      // Call parent handler first
      handleClientChange(newSelectedClients);
      // If projects exist and some clients were deselected, filter projects
      if (
        rawProjects &&
        rawProjects.length > 0 &&
        memoizedSelectedProjects.length > 0
      ) {
        const validProjects = selectors.getValidProjects(
          memoizedSelectedProjects,
          newSelectedClients,
          rawProjects
        );
        // If some projects need to be removed, update them
        if (validProjects.length !== memoizedSelectedProjects.length) {
          handleProjectChange(validProjects);
        }
      }
    },
    [
      handleClientChange,
      rawProjects,
      memoizedSelectedProjects,
      handleProjectChange,
    ]
  );

  // Memoize the filters object to prevent unnecessary history updates
  const currentFilters = useMemo(
    () => ({
      companyIds: memoizedSelectedClients,
      projectCodeIds: memoizedSelectedProjects,
    }),
    [memoizedSelectedClients, memoizedSelectedProjects]
  );

  // Update the URL state with the selected filters
  useEffect(() => {
    // Replace the current history entry with the updated filters
    history.replace(location.pathname + location.search, {
      filters: currentFilters,
    });
  }, [currentFilters, history, location.pathname, location.search]);

  return (
    <Card>
      <Row gutter={[16, 16]} align="middle" wrap={false}>
        <Col>
          <FilterOutlined style={{ fontSize: 20, color: "#0D9488" }} />
        </Col>
        {userRole !== "company_admin" &&
          userRole !== "regulatory_body_admin" &&
          userRole !== "csc_admin" && (
            <Col style={{ minWidth: 200, maxWidth: 300 }}>
              <Text strong>Client</Text>
              <Select
                mode="multiple"
                maxTagCount={0}
                maxTagPlaceholder={(omittedValues) =>
                  `${omittedValues.length} ${
                    omittedValues.length > 1 ? "clients" : "client"
                  } selected`
                }
                value={memoizedSelectedClients}
                placeholder="Select Client"
                style={{ width: "100%", marginTop: 4 }}
                options={clients || []}
                onChange={handleClientChangeWrapper}
                getPopupContainer={(triggerNode) => triggerNode.parentNode}
              />
            </Col>
          )}
        <Col style={{ minWidth: 200, maxWidth: 300 }}>
          <Text strong>Project</Text>
          <Select
            mode="multiple"
            maxTagCount={0}
            maxTagPlaceholder={(omittedValues) =>
              `${omittedValues.length} ${
                omittedValues.length > 1 ? "projects" : "project"
              } selected`
            }
            placeholder="Select Project"
            style={{ width: "100%", marginTop: 4 }}
            options={projects || []}
            disabled={!memoizedSelectedClients?.length}
            loading={isProjectsLoading}
            notFoundContent={
              isProjectsLoading ? <Spin size="small" /> : "No projects found"
            }
            value={memoizedSelectedProjects}
            onChange={handleProjectChange}
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
          />
        </Col>
        {showStateDropdown && (
          <Col style={{ minWidth: 220, maxWidth: 300 }}>
            <Text strong>State</Text>
            <Select
              mode="multiple"
              allowClear
              maxTagCount={0}
              maxTagPlaceholder={(omittedValues) =>
                `${omittedValues.length} ${
                  omittedValues.length > 1 ? "states" : "state"
                } selected`
              }
              placeholder="Filter by State"
              style={{ width: "100%", marginTop: 4 }}
              value={selectedState || []}
              notFoundContent={<Spin size="small" />}
              onChange={handleStateChange}
              options={extraStateOptions}
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
            />
          </Col>
        )}
        {showCompletionSort && (
          <Col style={{ minWidth: 150 }}>
            <Text strong>Sort</Text>
            <div style={{ marginTop: 4 }}>
              <Button
                type="primary"
                onClick={onToggleSort}
                icon={
                  <span
                    style={{
                      paddingRight: "12px",
                      display: "inline-block",
                      transform: isSortedDescending
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                    }}
                  >
                    ↑↓
                  </span>
                }
              >
                Completion
              </Button>
            </div>
          </Col>
        )}
        {showParamDropdowns && (
          <Col style={{ minWidth: 240, maxWidth: 300 }}>
            <Text strong>Commodity</Text>
            <Select
              mode="multiple"
              allowClear
              maxTagCount={0}
              maxTagPlaceholder={(omittedValues) =>
                `${omittedValues.length} ${
                  omittedValues.length > 1 ? "commodities" : "commodity"
                } selected`
              }
              placeholder="Select Commodity"
              style={{ width: "100%", marginTop: 4 }}
              value={memoizedSelectedCommodities}
              options={commodities || []}
              onChange={handleCommodityChange}
              notFoundContent={<Spin size="small" />}
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
            />
          </Col>
        )}
        {showParamDropdowns && (
          <Col style={{ minWidth: 220, maxWidth: 300 }}>
            <Text strong>Testing Type</Text>
            <Select
              mode="multiple"
              allowClear
              maxTagCount={0}
              maxTagPlaceholder={(omittedValues) =>
                `${omittedValues.length} ${
                  omittedValues.length > 1 ? "testing types" : "testing type"
                } selected`
              }
              placeholder="Select Testing Type"
              style={{ width: "100%", marginTop: 4 }}
              value={memoizedSelectedTestingTypes}
              options={testingTypes || []}
              onChange={handleTestingTypeChange}
              notFoundContent={<Spin size="small" />}
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
            />
          </Col>
        )}

        {currentView === "compliance" && (
          <>
            <Col flex="auto" />
            <Col>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "12px",
                  padding: "5px",
                }}
              >
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => handleParameterRequest(tab.key)}
                    style={{
                      backgroundColor:
                        parameterRequest === tab.key ? "white" : "transparent",
                      color: parameterRequest === tab.key ? "#1a1a1a" : "#666",
                      border: "none",
                      padding: "5px 10px",
                      borderRadius: "8px",
                      fontSize: "14px",
                      fontWeight: "500",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      fontFamily: "inherit",
                      boxShadow:
                        parameterRequest === tab.key
                          ? "0 2px 4px rgba(0,0,0,0.1)"
                          : "none",
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </Col>
          </>
        )}
      </Row>
    </Card>
  );
}
